import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Edit3, Globe, Eye, Package, Upload, ChevronDown, Shapes } from 'lucide-react';
import BackButton from './BackButton';
import LanguageFlags from './LanguageFlags';

const BookNavigationTabs = ({
  bookId,
  languageSelector,
  currentLanguage,
  headerTitle,
  headerLinkTo,
  showBackButton,
  showFlags = true,
  showBookSwitcher = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPlang =
    currentLanguage || new URLSearchParams(location.search).get('plang') || '';
  const showHeader = Boolean(headerTitle || showBackButton);
  const [openMenu, setOpenMenu] = useState(null);
  const [books, setBooks] = useState([]);
  const [booksLoading, setBooksLoading] = useState(false);

  useEffect(() => {
    if (!showBookSwitcher) return undefined;

    let isMounted = true;

    const loadBooks = async () => {
      setBooksLoading(true);
      try {
        const response = await axios.get('/api/list_books', {
          withCredentials: true,
        });
        if (!isMounted) return;
        setBooks(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        if (isMounted) {
          console.error('Failed to load books for navigation', error);
          setBooks([]);
        }
      } finally {
        if (isMounted) {
          setBooksLoading(false);
        }
      }
    };

    loadBooks();

    return () => {
      isMounted = false;
    };
  }, [showBookSwitcher]);

  const sortedBooks = useMemo(
    () =>
      [...books].sort((a, b) =>
        (a?.name || '').localeCompare(b?.name || '', undefined, {
          sensitivity: 'base',
        }),
      ),
    [books],
  );

  const currentBook = useMemo(
    () => sortedBooks.find((book) => book._id === bookId) || null,
    [sortedBooks, bookId],
  );
  const displayedBookTitle = currentBook?.name || headerTitle || 'Book';
  const bookOptions = useMemo(() => {
    if (!bookId) {
      return sortedBooks;
    }

    const currentOption =
      currentBook || { _id: bookId, name: displayedBookTitle };

    return [
      currentOption,
      ...sortedBooks.filter((book) => book._id !== currentOption._id),
    ];
  }, [bookId, currentBook, displayedBookTitle, sortedBooks]);

  const navigateToBook = (nextBookId) => {
    if (!nextBookId || nextBookId === bookId) return;

    if (location.pathname.startsWith('/create-pages-from-book')) {
      const params = new URLSearchParams(location.search);
      params.set('bookId', nextBookId);
      if (currentPlang) {
        params.set('plang', currentPlang);
      } else {
        params.delete('plang');
      }
      navigate(`/create-pages-from-book?${params.toString()}`);
      return;
    }

    if (location.pathname.startsWith('/pages/')) {
      navigate(`/pages/${nextBookId}${currentPlang ? `?plang=${currentPlang}` : ''}`);
      return;
    }

    if (location.pathname.startsWith('/preview-book/')) {
      navigate(`/preview-book/${nextBookId}${currentPlang ? `?plang=${currentPlang}` : ''}`);
      return;
    }

    if (location.pathname.startsWith('/release-book/')) {
      navigate(`/release-book/${nextBookId}${currentPlang ? `?plang=${currentPlang}` : ''}`);
    }
  };

  const tabs = useMemo(
    () => [
      {
        path: `/pages/${bookId}?plang=${currentPlang}`,
        label: 'Pages',
        icon: Edit3,
        match: (pathname) =>
          pathname === `/pages/${bookId}` ||
          pathname.startsWith(`/edit-page/${bookId}`),
      },
      {
        path: `/preview-book/${bookId}?plang=${currentPlang}`,
        label: 'Page renders',
        icon: Eye,
        match: (pathname) => pathname === `/preview-book/${bookId}`,
      },
      {
        path: `/release-book/${bookId}?plang=${currentPlang}`,
        label: 'Release',
        icon: Package,
        match: (pathname) =>
          pathname.startsWith(`/release-book/${bookId}`) ||
          pathname.startsWith(`/create-release/${bookId}`) ||
          pathname.startsWith(`/edit-release/${bookId}`),
      },
      {
        label: 'Book Actions',
        children: [
          {
            path: `/create-pages-from-book/?bookId=${bookId}&plang=${currentPlang}`,
            label: 'Add Pages',
            icon: Upload,
            match: (pathname) =>
              pathname === `/create-pages-from-book/` ||
              pathname === `/create-pages-from-book`,
          },
          {
            path: `/book-translations/${bookId}?plang=${currentPlang}`,
            label: 'Translations',
            icon: Globe,
            match: (pathname) => pathname === `/book-translations/${bookId}`,
          },
          {
            path: `/vector-layout/${bookId}/new`,
            label: 'Vector layouts',
            icon: Shapes,
            match: (pathname) => pathname.startsWith(`/vector-layout/${bookId}`),
          },
        ],
      },
    ],
    [bookId, currentPlang],
  );

  return (
    <div className="mb-6">
      {showHeader && (
        <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3">
            {showBackButton && (
              <BackButton className="mt-1 p-2 rounded hover:bg-gray-100" />
            )}
            <div>
              {showBookSwitcher ? (
                <div className="flex items-center gap-2">
                  <div className="relative inline-flex">
                    <select
                      value={bookId || ''}
                      onChange={(event) => navigateToBook(event.target.value)}
                      className="min-w-64 appearance-none rounded-xl border border-gray-200 bg-white px-4 py-2 pr-10 text-2xl font-bold text-gray-900 shadow-sm transition-colors focus:border-primary focus:outline-none"
                      disabled={booksLoading}
                    >
                      {booksLoading && (
                        <option value={bookId || ''}>{displayedBookTitle}</option>
                      )}
                      {!booksLoading &&
                        bookOptions.map((book) => (
                          <option key={book._id} value={book._id}>
                            {book.name}
                          </option>
                        ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />
                  </div>
                </div>
              ) : headerLinkTo ? (
                <Link
                  to={headerLinkTo}
                  className="text-2xl font-bold text-primary hover:text-primary/80 transition-colors"
                >
                  {headerTitle}
                </Link>
              ) : (
                <h1 className="text-2xl font-bold text-gray-900">
                  {headerTitle}
                </h1>
              )}
              {showFlags && (
                <LanguageFlags
                  language={currentLanguage}
                  className="mt-2 flex justify-center items-center gap-2"
                />
              )}
            </div>
          </div>
          {languageSelector && (
            <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
              {languageSelector}
            </div>
          )}
        </div>
      )}

      <nav className="relative overflow-visible border-b border-gray-200">
        {!showHeader && languageSelector && (
          <div className="mb-3 flex justify-end">
            <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
              {languageSelector}
            </div>
          </div>
        )}
        <div className="flex gap-6 overflow-visible">
          {tabs.map((tab) => {
            if (tab.children) {
              const isActive = tab.children.some((child) => child.match(location.pathname));

              return (
                <div
                  key={tab.label}
                  className="relative"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setOpenMenu((current) => (current === tab.label ? null : tab.label))
                    }
                    className={`flex items-center gap-2 px-1 py-3 border-b-2 transition-colors whitespace-nowrap ${
                      isActive
                        ? 'border-primary text-primary font-medium'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-sm">{tab.label}</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <div
                    className={`absolute right-0 top-[calc(100%-2px)] z-50 min-w-48 rounded-xl border border-gray-200 bg-white p-2 shadow-lg transition-all ${
                      openMenu === tab.label
                        ? 'visible opacity-100'
                        : 'invisible opacity-0'
                    }`}
                  >
                    {tab.children.map((child) => {
                      const ChildIcon = child.icon;
                      const childActive = child.match(location.pathname);

                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => setOpenMenu(null)}
                          className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                            childActive
                              ? 'bg-gray-100 text-primary font-medium'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <ChildIcon className="h-4 w-4" />
                          <span>{child.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            }

            const Icon = tab.icon;
            const isActive = tab.match(location.pathname);

            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={`flex items-center gap-2 px-1 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="text-sm">{tab.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

BookNavigationTabs.propTypes = {
  bookId: PropTypes.string.isRequired,
  languageSelector: PropTypes.node,
  currentLanguage: PropTypes.string,
  headerTitle: PropTypes.node,
  headerLinkTo: PropTypes.string,
  showBackButton: PropTypes.bool,
  showFlags: PropTypes.bool,
  showBookSwitcher: PropTypes.bool,
};

export default BookNavigationTabs;
